import React, { useMemo, useState } from 'react';

import { CalendarMonth } from './CalendarMonth';
import {
    addMonths,
    formatDateRangeLabel,
    formatRuDate,
    fromIsoDate,
    pluralRu,
    startOfDay,
    startOfMonth,
    toIsoDate,
    type DateRange,
    type IsoDate,
} from './dateUtils';
import styles from './DatePicker.module.scss';

export interface DateRangeCalendarProps {
    value: DateRange | null;
    onChange: (range: DateRange | null) => void;
    maxDate?: Date;
    endMaxDate?: Date | null;
    onStartPick?: (start: IsoDate) => void;
    maxMonths?: number | null;
    months?: 1 | 2;
}

export const DateRangeCalendar = ({
    value,
    onChange,
    maxDate,
    endMaxDate = null,
    onStartPick,
    maxMonths = null,
    months = 2,
}: DateRangeCalendarProps) => {
    const [draftStart, setDraftStart] = useState<Date | null>(null);
    const [hovered, setHovered] = useState<Date | null>(null);
    const [viewMonth, setViewMonth] = useState<Date>(() =>
        startOfMonth(value ? fromIsoDate(value.start) : new Date()),
    );

    const today = useMemo(() => startOfDay(new Date()), []);
    const limit = useMemo(() => startOfDay(maxDate ?? new Date()), [maxDate]);

    const handleDayClick = (day: Date) => {
        if (!draftStart) {
            setDraftStart(day);
            setHovered(null);
            onStartPick?.(toIsoDate(day));

            return;
        }

        const [start, end] = day < draftStart ? [day, draftStart] : [draftStart, day];

        onChange({ start: toIsoDate(start), end: toIsoDate(end) });
        setDraftStart(null);
        setHovered(null);
    };

    const handleReset = () => {
        setDraftStart(null);
        setHovered(null);
        onChange(null);
    };

    const [rangeFrom, rangeTo] = (() => {
        if (draftStart) {
            const other = hovered ?? draftStart;

            return other < draftStart ? [other, draftStart] : [draftStart, other];
        }

        return value ? [fromIsoDate(value.start), fromIsoDate(value.end)] : [null, null];
    })();

    const isDayDisabled = (day: Date) =>
        day > limit ||
        (Boolean(draftStart) && day < (draftStart as Date)) ||
        (Boolean(draftStart && endMaxDate) && day > (endMaxDate as Date));

    const shown = months === 2 ? [viewMonth, addMonths(viewMonth, 1)] : [viewMonth];
    const lastIndex = shown.length - 1;
    const canGoForward = startOfMonth(addMonths(viewMonth, lastIndex)) < startOfMonth(limit);

    return (
        <>
            <div className={styles.months}>
                {shown.map((month, monthIndex) => (
                    <CalendarMonth
                        key={month.toISOString()}
                        month={month}
                        today={today}
                        rangeFrom={rangeFrom}
                        rangeTo={rangeTo}
                        isDayDisabled={isDayDisabled}
                        onDayClick={handleDayClick}
                        onDayHover={(day) => draftStart && setHovered(day)}
                        onPrev={monthIndex === 0 ? () => setViewMonth(addMonths(viewMonth, -1)) : undefined}
                        onNext={monthIndex === lastIndex ? () => setViewMonth(addMonths(viewMonth, 1)) : undefined}
                        canNext={canGoForward}
                    />
                ))}
            </div>

            <div className={styles.popoverFooter}>
                <span className={styles.hint}>
                    {draftStart
                        ? endMaxDate
                            ? `Выберите конец периода — не позднее ${formatRuDate(toIsoDate(endMaxDate))}`
                            : 'Выберите конец периода'
                        : value
                            ? formatDateRangeLabel(value)
                            : maxMonths
                                ? `Выберите начало периода. Период — не более ${maxMonths} ${pluralRu(maxMonths, ['месяца', 'месяцев', 'месяцев'])}`
                                : 'Выберите начало периода'}
                </span>
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={handleReset}
                    disabled={!value && !draftStart}
                >
                    Сбросить
                </button>
            </div>
        </>
    );
};
